import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/GradientCard";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Search, CalendarDays, Inbox, ShieldCheck,
  BookOpen, Plus, AlertTriangle, CheckCircle2, XCircle, RefreshCcw, Info,
  FileText, Briefcase, Users, Clock, ClipboardList,
} from "lucide-react";
import { tabAccessibleFor, moduleCanDo } from "../lib/modules";
import { useGlobalViewMode } from "../components/ViewModeToggle";

const STATUS_TONES = {
  pending:  "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-500/20 dark:text-amber-200 dark:border-amber-500/40",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-500/20 dark:text-emerald-200 dark:border-emerald-500/40",
  rejected: "bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-500/20 dark:text-rose-200 dark:border-rose-500/40",
};

function fmt(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return d; }
}

function fmtDateTime(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return d; }
}

function todayIso() {
  const d = new Date();
  const iso = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  return iso;
}

// ────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────
export default function LeaveManagement({ actionPulse, embedded } = {}) {
  const { user } = useAuth();
  const isMaster = user?.role === "master_admin";
  const canManage = isMaster || moduleCanDo(user, "hr_leaves", "edit");

  const showMine = true;              // every user can view their own requests + apply
  const showApproval = tabAccessibleFor(user, "hr_leaves", "approval") || canManage;
  const showPolicy = true;            // policy is universal — visible to everyone
  const firstTab = showMine ? "mine" : showApproval ? "approval" : "policy";
  const [tab, setTab] = useState(firstTab);
  useEffect(() => { setTab(firstTab); }, [firstTab]);
  // v109.9.156 — Support deep-link `?tab=policy` (or any other tab) from
  // dashboard quick-actions that need to route to the policy tab silently.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && t !== tab) setTab(t);
    // Clean up URL so a refresh doesn't lock us onto this tab.
    if (t) {
      const next = new URLSearchParams(searchParams);
      next.delete("tab");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Policy ack
  const [ack, setAck] = useState({ acknowledged: false, acknowledged_at: null, policy_version: "" });
  const loadAck = useCallback(async () => {
    try {
      const { data } = await api.get("/leave/policy/my-status");
      setAck(data);
    } catch (e) { /* silent */ }
  }, []);
  useEffect(() => { loadAck(); }, [loadAck]);

  // My + All requests
  const [mine, setMine] = useState([]);
  const [allReqs, setAllReqs] = useState([]);
  const loadMine = useCallback(async () => {
    try {
      const { data } = await api.get("/leave/requests?scope=mine");
      setMine(data);
    } catch { /* silent */ }
  }, []);
  const loadAll = useCallback(async () => {
    try {
      const { data } = await api.get("/leave/requests?scope=all");
      setAllReqs(data);
    } catch { /* silent */ }
  }, []);
  useEffect(() => {
    if (showMine) loadMine();
    if (showApproval) loadAll();
  }, [showMine, showApproval, loadMine, loadAll]);

  const acknowledgePolicy = async () => {
    try {
      const { data } = await api.post("/leave/policy/acknowledge");
      setAck(data);
      toast.success("Policy acknowledged. You can now submit leave requests.");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  // New request dialog
  const [openNew, setOpenNew] = useState(false);

  // Review dialog
  const [reviewId, setReviewId] = useState(null);

  // Dashboard quick-action / deep-link support:
  //   - `actionPulse={{ key: "new", n: <ts> }}` (Dashboard QuickActionMount)
  //   - `?action=new` in URL (bookmark / notification "Open" link)
  useEffect(() => {
    if (actionPulse?.key === "new") {
      if (ack.acknowledged) setOpenNew(true);
      else {
        // v109.9.156 — Silent redirect to Policy tab (no toast per user spec).
        setTab("policy");
      }
    }
  }, [actionPulse?.key, actionPulse?.n, ack.acknowledged]);
  useEffect(() => {
    if (typeof window === "undefined" || embedded) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("action") === "new") {
      if (ack.acknowledged) setOpenNew(true);
      else setTab("policy");
    }
  }, [ack.acknowledged, embedded]);

  return (
    <div className="max-w-[1400px] space-y-7">
      <PageHeader
        moduleKey="hr_leaves"
        tone="emerald"
        icon={CalendarDays}
        eyebrow="HR Module"
        title="Leave Requests"
        subtitle="Apply, review approvals, and follow the leave policy — all in one place."
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap gap-1 bg-slate-100 dark:bg-slate-900/60 p-1 rounded-lg" data-testid="leave-tabs-list">
          {showMine && (
            <TabsTrigger value="mine" data-testid="leave-tab-mine" className="flex items-center gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Inbox className="w-3.5 h-3.5" /> My Requests
            </TabsTrigger>
          )}
          {showApproval && (
            <TabsTrigger value="approval" data-testid="leave-tab-approval" className="flex items-center gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <ShieldCheck className="w-3.5 h-3.5" /> All Requests
            </TabsTrigger>
          )}
          {showPolicy && (
            <TabsTrigger value="policy" data-testid="leave-tab-policy" className="flex items-center gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <BookOpen className="w-3.5 h-3.5" /> Policy
            </TabsTrigger>
          )}
        </TabsList>

        {showMine && (
          <TabsContent value="mine" className="mt-4 space-y-4" data-testid="leave-tab-content-mine">
            <MyRequestsTab
              mine={mine}
              ack={ack}
              userId={user?.id}
              onNew={() => {
                if (!ack.acknowledged) {
                  // v109.9.156 — Silent redirect to Policy tab.
                  setTab("policy");
                  return;
                }
                setOpenNew(true);
              }}
              onView={(r) => setReviewId(r.id)}
              onReload={loadMine}
            />
          </TabsContent>
        )}

        {showApproval && (
          <TabsContent value="approval" className="mt-4 space-y-4" data-testid="leave-tab-content-approval">
            <AllRequestsTab
              rows={allReqs}
              currentUserId={user?.id}
              isMaster={isMaster}
              onOpen={(r) => setReviewId(r.id)}
              onReload={loadAll}
            />
          </TabsContent>
        )}

        {showPolicy && (
          <TabsContent value="policy" className="mt-4 space-y-4" data-testid="leave-tab-content-policy">
            <PolicyTab
              ack={ack}
              onAcknowledge={acknowledgePolicy}
            />
          </TabsContent>
        )}
      </Tabs>

      {openNew && (
        <NewLeaveDialog
          open={openNew}
          onClose={() => setOpenNew(false)}
          onSuccess={() => {
            setOpenNew(false);
            loadMine();
            if (showApproval) loadAll();
          }}
        />
      )}

      {reviewId && (
        <ReviewLeaveDialog
          reqId={reviewId}
          currentUserId={user?.id}
          isMaster={isMaster}
          onClose={() => setReviewId(null)}
          onDecided={() => {
            setReviewId(null);
            loadMine();
            if (showApproval) loadAll();
          }}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// My Requests tab
// ────────────────────────────────────────────────────────────────────
function MyRequestsTab({ mine, ack, onNew, onView, onReload, userId }) {
  const [viewMode] = useGlobalViewMode();
  return (
    <>
      <LeaveBalanceCard userId={userId} />
      {!ack.acknowledged && (
        <div className="pk-card p-4 border-amber-300 border-l-4 flex items-start gap-3 bg-amber-50/60 dark:bg-amber-900/20" data-testid="policy-ack-warning">
          <AlertTriangle className="text-amber-600 mt-0.5" size={18} />
          <div className="flex-1">
            <div className="font-semibold text-amber-900 dark:text-amber-100 text-[13px]">Policy acknowledgment required</div>
            <div className="text-[12px] text-amber-800 dark:text-amber-200/90 mt-0.5">
              Please read and acknowledge the Leave Policy in the Policy tab before submitting any leave request.
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        <div className="text-[13px] text-slate-600 dark:text-slate-300">
          <b className="text-slate-900 dark:text-white">{mine.length}</b> request{mine.length !== 1 ? "s" : ""} submitted
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={onReload} className="h-9" data-testid="leave-mine-refresh">
            <RefreshCcw size={14} className="mr-1.5" /> Refresh
          </Button>
          <Button size="sm" onClick={onNew} disabled={!ack.acknowledged} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="leave-new-btn">
            <Plus size={14} className="mr-1.5" /> New Leave Request
          </Button>
        </div>
      </div>

      {mine.length === 0 ? (
        <div className="pk-card p-12 text-center text-slate-400 text-sm">No leave requests yet. Click &quot;New Leave Request&quot; to apply.</div>
      ) : viewMode === "card" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="leave-mine-cards">
          {mine.map((r) => (
            <div key={r.id} className="pk-card p-4 hover:shadow-md transition-shadow" data-testid={`leave-mine-card-${r.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-slate-900 dark:text-white">{r.kind_label || r.kind}</span>
                    {r.is_emergency && <Badge className="bg-rose-100 text-rose-800 dark:bg-rose-500/20 dark:text-rose-200 text-[10px]">EMERGENCY</Badge>}
                  </div>
                  <div className="text-[11.5px] text-slate-600 dark:text-slate-300 mt-1">{fmt(r.from_date)} <span className="text-slate-400">→</span> {fmt(r.to_date)}</div>
                </div>
                <Badge className={(STATUS_TONES[r.status] || "") + " shrink-0"}>{r.status}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[11.5px] mt-2">
                <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Days</div><div className="mono font-semibold text-slate-800 dark:text-slate-100">{r.leave_days}</div></div>
                <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Approver</div><div className="text-slate-800 dark:text-slate-100 truncate">{r.approver_name || "—"}</div></div>
                <div className="col-span-2"><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Reason</div><div className="text-slate-700 dark:text-slate-300 line-clamp-2" title={r.reason}>{r.reason}</div></div>
              </div>
              <div className="pt-2 mt-2 border-t border-slate-100 dark:border-slate-800">
                <Button size="sm" variant="outline" className="h-7 text-[11px] w-full" onClick={() => onView(r)} data-testid={`leave-mine-view-${r.id}`}>View</Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
      <div className="pk-card overflow-x-auto">
        <table className="w-full text-sm responsive-table">
          <thead className="border-b border-slate-200 bg-slate-50/60 dark:bg-slate-900/40">
            <tr>
              <th className="px-4 py-3 text-left pk-table-header">Type</th>
              <th className="px-4 py-3 text-left pk-table-header">From → To</th>
              <th className="px-4 py-3 text-center pk-table-header">Days</th>
              <th className="px-4 py-3 text-left pk-table-header">Reason</th>
              <th className="px-4 py-3 text-left pk-table-header">Approver</th>
              <th className="px-4 py-3 text-center pk-table-header">Status</th>
              <th className="px-4 py-3 text-right pk-table-header">Actions</th>
            </tr>
          </thead>
          <tbody>
            {mine.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-slate-400 text-sm">No leave requests yet. Click &quot;New Leave Request&quot; to apply.</td></tr>
            )}
            {mine.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/50 dark:hover:bg-slate-900/40" data-testid={`leave-mine-row-${r.id}`}>
                <td className="px-4 py-3" data-label="Type">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900 dark:text-white text-[13px]">{r.kind_label || r.kind}</span>
                    {r.is_emergency && <Badge className="bg-rose-100 text-rose-800 dark:bg-rose-500/20 dark:text-rose-200 border-rose-200 dark:border-rose-500/40 text-[10px]">EMERGENCY</Badge>}
                  </div>
                </td>
                <td className="px-4 py-3 text-[12.5px] text-slate-700 dark:text-slate-300" data-label="From → To">
                  {fmt(r.from_date)} <span className="text-slate-400">→</span> {fmt(r.to_date)}
                </td>
                <td className="px-4 py-3 text-center mono font-semibold text-slate-900 dark:text-white" data-label="Days">{r.leave_days}</td>
                <td className="px-4 py-3 text-[12.5px] text-slate-600 dark:text-slate-300 max-w-xs truncate" data-label="Reason" title={r.reason}>{r.reason}</td>
                <td className="px-4 py-3 text-[12px] text-slate-600 dark:text-slate-300" data-label="Approver">{r.approver_name || "—"}</td>
                <td className="px-4 py-3 text-center" data-label="Status">
                  <Badge className={STATUS_TONES[r.status] || ""}>{r.status}</Badge>
                </td>
                <td className="px-4 py-3 text-right" data-label="Actions">
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => onView(r)} data-testid={`leave-mine-view-${r.id}`}>
                    View
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────
// All Requests / Approval tab
// ────────────────────────────────────────────────────────────────────
function AllRequestsTab({ rows, currentUserId, isMaster, onOpen, onReload }) {
  const [viewMode] = useGlobalViewMode();
  const [statusFilter, setStatusFilter] = useState("pending");
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (!s) return true;
      return [r.user_name, r.user_email, r.employee_code, r.kind_label, r.reason].some((v) =>
        String(v ?? "").toLowerCase().includes(s)
      );
    });
  }, [rows, q, statusFilter]);

  const pendingCount = rows.filter((r) => r.status === "pending" && r.user_id !== currentUserId).length;

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-[13px] text-slate-600 dark:text-slate-300">
          <b className="text-slate-900 dark:text-white">{pendingCount}</b> pending your review
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employee, kind…" className="pl-9 h-9 w-64" data-testid="leave-all-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-36" data-testid="leave-all-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={onReload} className="h-9" data-testid="leave-all-refresh">
            <RefreshCcw size={14} className="mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="pk-card p-12 text-center text-slate-400 text-sm">No requests to show.</div>
      ) : viewMode === "card" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="leave-all-cards">
          {filtered.map((r) => {
            const isOwn = r.user_id === currentUserId;
            const canDecide = r.status === "pending" && (isMaster || (r.approver_id === currentUserId && !isOwn));
            return (
              <div key={r.id} className="pk-card p-4 hover:shadow-md transition-shadow" data-testid={`leave-all-card-${r.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-slate-900 dark:text-white truncate">{r.user_name}</div>
                    <div className="text-[10.5px] mono text-slate-500 dark:text-slate-400 truncate">{r.employee_code || r.user_email}</div>
                  </div>
                  <Badge className={(STATUS_TONES[r.status] || "") + " shrink-0"}>{r.status}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 text-[11.5px] mt-2">
                  <div className="col-span-2 flex items-center gap-1.5"><span className="font-semibold text-slate-900 dark:text-white">{r.kind_label || r.kind}</span>{r.is_emergency && <Badge className="bg-rose-100 text-rose-800 dark:bg-rose-500/20 dark:text-rose-200 text-[10px]">EMERG</Badge>}</div>
                  <div className="col-span-2"><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Dates</div><div className="text-slate-800 dark:text-slate-100">{fmt(r.from_date)} → {fmt(r.to_date)}</div></div>
                  <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Days</div><div className="mono font-semibold text-slate-900 dark:text-white">{r.leave_days}</div></div>
                  <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Approver</div><div className="text-slate-800 dark:text-slate-100 truncate">{r.approver_name || "—"}</div></div>
                </div>
                <div className="pt-2 mt-2 border-t border-slate-100 dark:border-slate-800">
                  <Button size="sm" variant="outline" className="h-7 text-[11px] w-full" onClick={() => onOpen(r)} data-testid={`leave-all-review-${r.id}`}>
                    {canDecide ? "Review" : "View"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
      <div className="pk-card overflow-x-auto">
        <table className="w-full text-sm responsive-table">
          <thead className="border-b border-slate-200 bg-slate-50/60 dark:bg-slate-900/40">
            <tr>
              <th className="px-4 py-3 text-left pk-table-header">Employee</th>
              <th className="px-4 py-3 text-left pk-table-header">Type</th>
              <th className="px-4 py-3 text-left pk-table-header">Dates</th>
              <th className="px-4 py-3 text-center pk-table-header">Days</th>
              <th className="px-4 py-3 text-left pk-table-header">Approver</th>
              <th className="px-4 py-3 text-center pk-table-header">Status</th>
              <th className="px-4 py-3 text-right pk-table-header">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-slate-400 text-sm">No requests to show.</td></tr>
            )}
            {filtered.map((r) => {
              const isOwn = r.user_id === currentUserId;
              const canDecide = r.status === "pending" && (isMaster || (r.approver_id === currentUserId && !isOwn));
              return (
                <tr key={r.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/50 dark:hover:bg-slate-900/40" data-testid={`leave-all-row-${r.id}`}>
                  <td className="px-4 py-3" data-label="Employee">
                    <div className="font-semibold text-slate-900 dark:text-white text-[13px]">{r.user_name}</div>
                    <div className="text-[11px] mono text-slate-500 dark:text-slate-400">{r.employee_code || r.user_email}</div>
                  </td>
                  <td className="px-4 py-3" data-label="Type">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[12.5px] font-medium text-slate-900 dark:text-white">{r.kind_label || r.kind}</span>
                      {r.is_emergency && <Badge className="bg-rose-100 text-rose-800 dark:bg-rose-500/20 dark:text-rose-200 text-[10px]">EMERG</Badge>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[12px] text-slate-700 dark:text-slate-300" data-label="Dates">
                    {fmt(r.from_date)} → {fmt(r.to_date)}
                  </td>
                  <td className="px-4 py-3 text-center mono font-semibold text-slate-900 dark:text-white" data-label="Days">{r.leave_days}</td>
                  <td className="px-4 py-3 text-[12px] text-slate-600 dark:text-slate-300" data-label="Approver">{r.approver_name || "—"}</td>
                  <td className="px-4 py-3 text-center" data-label="Status">
                    <Badge className={STATUS_TONES[r.status] || ""}>{r.status}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right" data-label="Actions">
                    <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => onOpen(r)} data-testid={`leave-all-review-${r.id}`}>
                      {canDecide ? "Review" : "View"}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────
// Policy tab — interactive 14-section policy (accordion) + short summary
// ────────────────────────────────────────────────────────────────────
function PolicyTab({ ack, onAcknowledge }) {
  return (
    <>
      <div className="pk-card p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <BookOpen className="text-emerald-600" size={20} />
            <div>
              <h3 className="heading text-lg font-bold text-slate-900 dark:text-white">Leave Application Rules</h3>
              <div className="text-[11px] mono text-slate-500 dark:text-slate-400">Panchakosh Ventures · Policy v{ack.policy_version || "—"}</div>
            </div>
          </div>
          {ack.acknowledged ? (
            <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200 border-emerald-200 dark:border-emerald-500/40 h-8 px-3" data-testid="leave-policy-ack-status">
              <CheckCircle2 size={13} className="mr-1.5" /> Acknowledged on {fmtDateTime(ack.acknowledged_at)}
            </Badge>
          ) : (
            <Button onClick={onAcknowledge} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="leave-policy-ack-btn">
              I acknowledge the Policy
            </Button>
          )}
        </div>

        {/* Short summary banner */}
        <div className="mt-5 pk-card p-4 bg-emerald-50/60 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 border-l-4">
          <div className="flex items-start gap-2">
            <Info className="text-emerald-600 mt-0.5 flex-shrink-0" size={16} />
            <div className="text-[12.5px] text-emerald-900 dark:text-emerald-100 leading-relaxed">
              <b>Short Leave Policy Statement.</b> Employees must apply for leave through the prescribed company process and obtain prior approval from their Reporting Manager. Emergency leave should be reported immediately. Unapproved absence may be treated as <b>Absent (A)</b> or <b>Leave Without Pay (LWP)</b>. Leave approval is subject to business requirements, staffing availability, and the employee&apos;s leave balance.
            </div>
          </div>
        </div>

        {/* Interactive 14-section accordion */}
        <div className="mt-5">
          <PolicyAccordion />
        </div>
      </div>
    </>
  );
}

function PolicyAccordion() {
  const sections = [
    {
      id: "s1", icon: ShieldCheck, tone: "indigo", title: "1. Prior Approval",
      body: (
        <ul className="list-disc pl-6 space-y-1">
          <li>All planned leave must be approved by the <b>Reporting Manager</b> before availing the leave.</li>
          <li>Leave without approval may be treated as <b>Absent (A)</b> or <b>Leave Without Pay (LWP)</b>.</li>
        </ul>
      ),
    },
    {
      id: "s2", icon: Clock, tone: "sky", title: "2. Advance Notice",
      body: (
        <div className="overflow-x-auto">
          <table className="text-[12.5px] border-collapse w-full max-w-2xl">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/60">
                <th className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-left">Leave Duration</th>
                <th className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-left">Notice Required</th>
              </tr>
            </thead>
            <tbody>
              <tr><td className="border border-slate-200 dark:border-slate-700 px-3 py-2">Half Day</td><td className="border border-slate-200 dark:border-slate-700 px-3 py-2 mono">At least 1 day in advance</td></tr>
              <tr><td className="border border-slate-200 dark:border-slate-700 px-3 py-2">1 Day Leave</td><td className="border border-slate-200 dark:border-slate-700 px-3 py-2 mono">At least 1 day in advance</td></tr>
              <tr><td className="border border-slate-200 dark:border-slate-700 px-3 py-2">2–3 Days Leave</td><td className="border border-slate-200 dark:border-slate-700 px-3 py-2 mono">At least 3 working days in advance</td></tr>
              <tr><td className="border border-slate-200 dark:border-slate-700 px-3 py-2">More than 3 Days</td><td className="border border-slate-200 dark:border-slate-700 px-3 py-2 mono">At least 7 working days in advance</td></tr>
            </tbody>
          </table>
        </div>
      ),
    },
    {
      id: "s3", icon: AlertTriangle, tone: "amber", title: "3. Emergency Leave",
      body: (
        <ul className="list-disc pl-6 space-y-1">
          <li>In case of illness or an emergency, the employee must inform the Reporting Manager <b>before the start of the workday</b> or as soon as reasonably possible.</li>
          <li>A formal leave request should be submitted within <b>24 hours</b>.</li>
        </ul>
      ),
    },
    {
      id: "s4", icon: FileText, tone: "rose", title: "4. Sick Leave",
      body: (
        <ul className="list-disc pl-6 space-y-1">
          <li>Sick Leave may be taken only when genuinely unwell.</li>
          <li>A medical certificate may be required for more than <b>2 consecutive working days</b> of Sick Leave.</li>
        </ul>
      ),
    },
    {
      id: "s5", icon: CalendarDays, tone: "sky", title: "5. Casual Leave",
      body: (
        <ul className="list-disc pl-6 space-y-1">
          <li>Casual Leave is for personal work, family functions, urgent personal matters, etc.</li>
          <li>Casual Leave should normally not exceed <b>3 consecutive days</b> unless approved.</li>
        </ul>
      ),
    },
    {
      id: "s6", icon: Briefcase, tone: "emerald", title: "6. Earned Leave",
      body: (
        <ul className="list-disc pl-6 space-y-1">
          <li>Earned Leave should be planned in advance.</li>
          <li>Approval depends on project requirements and business needs.</li>
          <li>Employees are encouraged to apply <b>at least 7 days in advance</b>.</li>
        </ul>
      ),
    },
    {
      id: "s7", icon: Clock, tone: "violet", title: "7. Half-Day Leave",
      body: (
        <ul className="list-disc pl-6 space-y-1">
          <li>Half-day leave must be approved by the Reporting Manager.</li>
          <li>Two half-day leaves are treated as one full day&apos;s leave.</li>
        </ul>
      ),
    },
    {
      id: "s8", icon: Users, tone: "amber", title: "8. Leave During Probation",
      body: (
        <ul className="list-disc pl-6 space-y-1">
          <li>Leave during probation should be avoided except in genuine emergencies.</li>
          <li>Any leave beyond the available balance will be treated as <b>Leave Without Pay (LWP)</b> unless otherwise approved.</li>
        </ul>
      ),
    },
    {
      id: "s9", icon: XCircle, tone: "rose", title: "9. Leave During Notice Period",
      body: (
        <ul className="list-disc pl-6 space-y-1">
          <li>Leave during the notice period is generally <b>not permitted</b>.</li>
          <li>Any exception requires written approval from Management or HR.</li>
          <li>Unapproved leave may extend the notice period or be treated as LWP, as provided in the employment agreement.</li>
        </ul>
      ),
    },
    {
      id: "s10", icon: AlertTriangle, tone: "rose", title: "10. Absence Without Information",
      body: (
        <div className="space-y-2">
          <div className="text-[12.5px]">If an employee is absent without informing the Reporting Manager:</div>
          <ul className="list-disc pl-6 space-y-1">
            <li><b>1 Day:</b> Marked as <Badge className="bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100 mx-1">Absent (A)</Badge>.</li>
            <li><b>2 Consecutive Days:</b> HR will contact the employee.</li>
            <li><b>3 Consecutive Working Days:</b> May be treated as <b>unauthorized absence</b> and may lead to disciplinary action, including initiation of job abandonment procedures after following due process.</li>
          </ul>
        </div>
      ),
    },
    {
      id: "s11", icon: RefreshCcw, tone: "sky", title: "11. Leave Cancellation",
      body: (
        <p>Approved leave may be cancelled or rescheduled with the Reporting Manager&apos;s approval if business requirements permit.</p>
      ),
    },
    {
      id: "s12", icon: CalendarDays, tone: "emerald", title: "12. Public Holidays &amp; Weekly Off",
      body: (
        <p>Public Holidays and Weekly Offs cannot be converted into additional leave unless specifically approved under a <b>compensatory off</b> policy.</p>
      ),
    },
    {
      id: "s13", icon: ClipboardList, tone: "indigo", title: "13. Leave Balance",
      body: (
        <ul className="list-disc pl-6 space-y-1">
          <li>Employees are responsible for checking their leave balance before applying for leave.</li>
          <li>If there is insufficient leave balance, the leave will be treated as <b>LWP</b>, unless otherwise approved.</li>
        </ul>
      ),
    },
    {
      id: "s14", icon: XCircle, tone: "rose", title: "14. False Information",
      body: (
        <p>Submitting false medical certificates or misusing leave may result in <b>disciplinary action</b>.</p>
      ),
    },
  ];

  const [open, setOpen] = useState(["s1", "s2"]);

  const toneMap = {
    indigo:  { chip: "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-200",   left: "border-l-indigo-500" },
    sky:     { chip: "bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-200",               left: "border-l-sky-500" },
    amber:   { chip: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-200",       left: "border-l-amber-500" },
    rose:    { chip: "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-200",           left: "border-l-rose-500" },
    emerald: { chip: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200", left: "border-l-emerald-500" },
    violet:  { chip: "bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-200",   left: "border-l-violet-500" },
  };

  return (
    <>
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="text-[11.5px] text-slate-500 dark:text-slate-400">
          {open.length} of {sections.length} section{sections.length !== 1 ? "s" : ""} expanded
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setOpen(sections.map((s) => s.id))} className="h-8 text-[11.5px]" data-testid="policy-expand-all">Expand All</Button>
          <Button variant="outline" size="sm" onClick={() => setOpen([])} className="h-8 text-[11.5px]" data-testid="policy-collapse-all">Collapse All</Button>
        </div>
      </div>
      <Accordion type="multiple" value={open} onValueChange={setOpen} className="space-y-2">
        {sections.map((s) => {
          const Icon = s.icon;
          const t = toneMap[s.tone] || toneMap.indigo;
          return (
            <AccordionItem
              key={s.id}
              value={s.id}
              className={`pk-card border-l-4 ${t.left} border-b-0 rounded-lg overflow-hidden`}
              data-testid={`policy-section-${s.id}`}
            >
              <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-slate-50/60 dark:hover:bg-slate-900/40">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${t.chip}`}>
                    <Icon size={15} />
                  </div>
                  <div className="text-[13.5px] font-semibold text-slate-900 dark:text-white text-left" dangerouslySetInnerHTML={{ __html: s.title }} />
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4 pt-0 text-[12.5px] text-slate-700 dark:text-slate-300 leading-relaxed">
                <div className="pl-11">{s.body}</div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────
// Leave Balance card (v109.9.147 — single source of truth via
// /api/my-leave-balance which returns the CURRENT-MONTH CLOSING from the
// Payroll ledger. Dashboard card + this card + Payroll → Leave Balance
// tab all agree.)
// ────────────────────────────────────────────────────────────────────
function LeaveBalanceCard({ userId }) {
  const [bal, setBal] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    if (!userId) return;
    try {
      const { data } = await api.get("/my-leave-balance");
      setBal({
        cl: data.casual ?? 0,
        sl: data.sick ?? 0,
        el: data.earned ?? 0,
      });
      setErr("");
    } catch (e) {
      setErr(formatError(e.response?.data?.detail) || "Could not load leave balance");
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const monthLabel = new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" });

  return (
    <div className="pk-card p-5" data-testid="leave-balance-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <CalendarDays className="text-emerald-600" size={16} />
          <div className="text-[13.5px] font-bold text-slate-900 dark:text-white">Leave Balance</div>
          <Badge className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-[10.5px]">{monthLabel}</Badge>
        </div>
        <div className="text-[10.5px] text-slate-500 dark:text-slate-400">Current-month closing · Payroll ledger</div>
      </div>
      {err && <div className="text-[12px] text-amber-700 dark:text-amber-300 italic">{err}</div>}
      {!err && !bal && <div className="text-[12px] text-slate-500 italic">Loading…</div>}
      {bal && (
        <div className="grid grid-cols-3 gap-3">
          <BalanceCell tone="sky"     label="Casual (CL)" balance={bal.cl} />
          <BalanceCell tone="rose"    label="Sick (SL)"   balance={bal.sl} />
          <BalanceCell tone="emerald" label="Earned (EL)" balance={bal.el} />
        </div>
      )}
    </div>
  );
}

function BalanceCell({ tone, label, balance }) {
  const toneMap = {
    sky:     "bg-sky-50 dark:bg-sky-950/30 border-sky-200 dark:border-sky-900",
    rose:    "bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-900",
    emerald: "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900",
  };
  return (
    <div className={`rounded-lg border p-3 ${toneMap[tone] || toneMap.sky}`}>
      <div className="pk-table-header">{label}</div>
      <div className="heading text-2xl font-extrabold text-slate-900 dark:text-white mono mt-1">{Number(balance ?? 0).toFixed(1)}</div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// New leave dialog — uses the "old format" fields:
//   Leave Type (Casual/Sick/Earned/Unpaid) · Is Half-Day (No/Yes)
//   Half Session (1st Half/2nd Half) · Date/From · To · No. of Days · Reason
//   + Emergency toggle (CL/EL/LWP only) + Medical cert (Sick only)
// ────────────────────────────────────────────────────────────────────
const LEAVE_TYPES = [
  { value: "casual", label: "Casual" },
  { value: "sick",   label: "Sick" },
  { value: "earned", label: "Earned" },
  { value: "lwp",    label: "Unpaid" },
];

function resolveKind(leaveType, isHalfDay) {
  if (isHalfDay === "Yes") {
    return leaveType === "casual" ? "half_casual"
      : leaveType === "sick"   ? "half_sick"
      : leaveType === "earned" ? "half_earned"
      : leaveType === "lwp"    ? "half_lwp"
      : "half_casual";
  }
  return leaveType;
}

function NewLeaveDialog({ open, onClose, onSuccess }) {
  const { user } = useAuth();
  const [leaveType, setLeaveType] = useState("casual");
  const [isHalfDay, setIsHalfDay] = useState("No");
  const [halfSession, setHalfSession] = useState("1st Half");
  const [fromDate, setFromDate] = useState(todayIso());
  const [toDate, setToDate] = useState(todayIso());
  const [reason, setReason] = useState("");
  const [isEmergency, setIsEmergency] = useState(false);
  const [emergencyReason, setEmergencyReason] = useState("");
  const [medCert, setMedCert] = useState("");
  const [saving, setSaving] = useState(false);
  // v109.9.142 — live balance for the chip inside the dialog.
  const [liveBal, setLiveBal] = useState(null);
  useEffect(() => {
    let mounted = true;
    api.get("/my-leave-balance").then(({ data }) => { if (mounted) setLiveBal(data); }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  const isHalf = isHalfDay === "Yes";
  const kind = resolveKind(leaveType, isHalfDay);
  const isSick = leaveType === "sick";
  const canEmergency = ["casual", "earned", "lwp"].includes(leaveType);

  // v109.9.135/138 — Earliest allowed date.
  //   On or before the 10th → 1st of PREVIOUS month (grace window).
  //   From the 11th onwards → 1st of CURRENT month.
  const minAllowedDate = useMemo(() => {
    const t = new Date();
    const useGrace = t.getDate() <= 10;
    const target = useGrace
      ? new Date(t.getFullYear(), t.getMonth() - 1, 1)
      : new Date(t.getFullYear(), t.getMonth(), 1);
    return `${target.getFullYear()}-${String(target.getMonth() + 1).padStart(2, "0")}-01`;
  }, []);

  // Live client-side days count
  const daysCount = useMemo(() => {
    if (isHalf) return 0.5;
    try {
      const f = new Date(fromDate);
      const t = new Date(toDate);
      const diff = Math.max(0, Math.floor((t - f) / 86400000)) + 1;
      return diff;
    } catch { return 0; }
  }, [isHalf, fromDate, toDate]);

  const noticeNeeded = useMemo(() => {
    if (isHalf) return 1;
    if (daysCount <= 1) return 1;
    if (daysCount <= 3) return 3;
    return 7;
  }, [isHalf, daysCount]);

  const submit = async () => {
    if (reason.trim().length < 3) { toast.error("Please add a reason (min 3 characters)."); return; }
    if (isEmergency && emergencyReason.trim().length < 10) {
      toast.error("Emergency reason (min 10 characters) required."); return;
    }
    // v109.9.135/138 — Enforce the earliest-allowed-date rule client-side.
    if (fromDate < minAllowedDate || (!isHalf && toDate < minAllowedDate)) {
      const t = new Date();
      const graceActive = t.getDate() <= 10;
      const msg = graceActive
        ? `On or before the 10th, leaves may be applied for the previous month, current month, or future months (earliest: ${minAllowedDate}).`
        : `After the 10th, leaves may only be applied for the current or future months (earliest: ${minAllowedDate}).`;
      toast.error(msg);
      return;
    }
    setSaving(true);
    try {
      const res = await api.post("/leave/requests", {
        kind,
        from_date: fromDate,
        to_date: isHalf ? fromDate : toDate,
        halfday_session: isHalf ? halfSession : "",
        reason: reason.trim(),
        is_emergency: isEmergency && canEmergency,
        emergency_reason: emergencyReason.trim(),
        medical_cert_link: medCert.trim(),
      });
      if (res?.data?.auto_converted_to_lwp) {
        toast.warning(
          res.data.conversion_note ||
            `Your ${res.data.original_kind_label || "leave"} balance was insufficient. Auto-converted to ${res.data.kind_label}.`,
          { duration: 8000 }
        );
      } else {
        toast.success("Leave request submitted");
      }
      onSuccess();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not submit");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="leave-new-dialog">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2">
            <Plus size={18} /> New Leave Request
          </DialogTitle>
          <DialogDescription>Routes to your Reporting Manager (or Master Admin if none is set).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {/* Employee (read-only, auto) */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold">Employee Name</Label>
              <Input readOnly value={user?.name || ""} className="mt-1.5 h-10 bg-slate-50 dark:bg-slate-900/40" data-testid="leave-new-emp-name" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold">Employee Code</Label>
              <Input readOnly value={user?.employee_code || ""} className="mt-1.5 h-10 bg-slate-50 dark:bg-slate-900/40 mono" data-testid="leave-new-emp-code" />
            </div>
          </div>

          {/* Leave Type */}
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <Label className="text-[11px] uppercase tracking-wider font-semibold">Leave Type <span className="text-rose-500">*</span></Label>
              {/* v109.9.142 — Live balance chip for the currently-selected kind */}
              {liveBal && leaveType !== "lwp" && (() => {
                const bucket = leaveType;
                const avail = Number(liveBal?.[bucket] ?? 0);
                const need  = isHalf ? 0.5 : Math.max(1, daysCount || 1);
                const short = need > avail;
                return (
                  <span
                    className={`ml-auto inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10.5px] font-semibold border ${
                      short
                        ? "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-500/20 dark:text-amber-100 dark:border-amber-500/40"
                        : "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-500/20 dark:text-emerald-200 dark:border-emerald-500/40"
                    }`}
                    data-testid="leave-live-balance-chip"
                    title={short ? "Insufficient — will auto-convert to LWP" : "Available balance"}
                  >
                    Balance: <span className="font-mono">{avail.toFixed(1)}</span> · Need: <span className="font-mono">{need}</span>
                    {short && <span className="ml-1">→ LWP</span>}
                  </span>
                );
              })()}
            </div>
            <Select value={leaveType} onValueChange={setLeaveType}>
              <SelectTrigger className="mt-1.5 h-10" data-testid="leave-new-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LEAVE_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Is Half-Day */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold">Is this a Half-Day leave?</Label>
              <Select value={isHalfDay} onValueChange={setIsHalfDay}>
                <SelectTrigger className="mt-1.5 h-10" data-testid="leave-new-halfday"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="No">No</SelectItem>
                  <SelectItem value="Yes">Yes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isHalf && (
              <div>
                <Label className="text-[11px] uppercase tracking-wider font-semibold">Which half?</Label>
                <Select value={halfSession} onValueChange={setHalfSession}>
                  <SelectTrigger className="mt-1.5 h-10" data-testid="leave-new-halfsession"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1st Half">1st Half</SelectItem>
                    <SelectItem value="2nd Half">2nd Half</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold">{isHalf ? "Date" : "From"} <span className="text-rose-500">*</span></Label>
              <Input type="date" min={minAllowedDate} value={fromDate} onChange={(e) => { setFromDate(e.target.value); if (isHalf || new Date(e.target.value) > new Date(toDate)) setToDate(e.target.value); }} className="mt-1.5 h-10 mono" data-testid="leave-new-from" />
            </div>
            {!isHalf && (
              <div>
                <Label className="text-[11px] uppercase tracking-wider font-semibold">To <span className="text-rose-500">*</span></Label>
                <Input type="date" min={minAllowedDate} value={toDate} onChange={(e) => setToDate(e.target.value)} className="mt-1.5 h-10 mono" data-testid="leave-new-to" />
              </div>
            )}
          </div>

          {/* Days count (auto/readonly) + notice hint */}
          <div className="pk-card p-3 bg-slate-50/70 dark:bg-slate-900/40 flex items-center justify-between text-[12px]">
            <div className="flex items-center gap-2">
              <Info size={13} className="text-emerald-600" />
              <span className="text-slate-700 dark:text-slate-300">No. of Days: <b className="text-slate-900 dark:text-white mono">{daysCount}</b></span>
            </div>
            {canEmergency && !isEmergency && (
              <span className="text-slate-600 dark:text-slate-300">Requires <b className="text-slate-900 dark:text-white mono">{noticeNeeded}</b> working day(s) advance notice</span>
            )}
            {isSick && <Badge className="bg-sky-100 text-sky-800 dark:bg-sky-500/20 dark:text-sky-200">Sick — past dates in current month allowed</Badge>}
          </div>

          {/* Reason */}
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold">Reason <span className="text-slate-400 normal-case">(min 3 chars)</span></Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Purpose of leave…" className="mt-1.5" data-testid="leave-new-reason" />
          </div>

          {/* Unpaid Leave Warning */}
          {leaveType === "lwp" && (
            <div className="pk-card p-4 border-rose-300 dark:border-rose-800 border-l-4 bg-rose-50/60 dark:bg-rose-900/20" data-testid="leave-new-lwp-warning">
              <div className="flex items-start gap-2">
                <AlertTriangle className="text-rose-600 mt-0.5 flex-shrink-0" size={16} />
                <div className="flex-1 text-[12.5px] text-rose-900 dark:text-rose-100 leading-relaxed">
                  <div className="font-bold text-[13px] mb-1">Unpaid Leave (LWP) selected</div>
                  <p className="mb-2">
                    Please make sure this leave is <b>approved by your Reporting Manager</b>. Without approval, it will be treated as <b>Absence Without Information</b>:
                  </p>
                  <ul className="list-disc pl-5 space-y-0.5">
                    <li><b>1 Day:</b> Marked as <span className="mono">Absent (A)</span>.</li>
                    <li><b>2 Consecutive Days:</b> HR will contact the employee.</li>
                    <li><b>3 Consecutive Working Days:</b> May be treated as <b>unauthorized absence</b> and may lead to disciplinary action, including initiation of job abandonment procedures after following due process.</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Emergency (only for CL/EL/LWP) */}
          {canEmergency && (
            <div className="pk-card p-3 border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-900/20">
              <label className="flex items-start gap-2 cursor-pointer" data-testid="leave-new-emergency-toggle">
                <Checkbox checked={isEmergency} onCheckedChange={(v) => setIsEmergency(!!v)} className="mt-0.5" />
                <div className="flex-1">
                  <div className="text-[12.5px] font-semibold text-amber-900 dark:text-amber-100">Mark as Emergency (bypass advance notice)</div>
                  <div className="text-[11.5px] text-amber-800/90 dark:text-amber-200/80">Use only for genuine emergencies. Requires written reason.</div>
                </div>
              </label>
              {isEmergency && (
                <Textarea
                  value={emergencyReason}
                  onChange={(e) => setEmergencyReason(e.target.value)}
                  rows={2}
                  placeholder="Describe the emergency (min 10 chars)…"
                  className="mt-2"
                  data-testid="leave-new-emergency-reason"
                />
              )}
            </div>
          )}

          {/* Medical cert (only for Sick) */}
          {isSick && (
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold">Medical certificate link <span className="text-slate-400 normal-case">(required if &gt; 3 days)</span></Label>
              <Input value={medCert} onChange={(e) => setMedCert(e.target.value)} placeholder="https://drive.google.com/…" className="mt-1.5 h-10" data-testid="leave-new-medcert" />
              <div className="text-[11px] text-slate-500 mt-1">Without a valid certificate, days beyond 3 may be converted to LWP by HR.</div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="leave-new-submit">
            {saving ? "Submitting…" : "Submit Request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────
// Review dialog (approve / reject / reassign + context sidebar)
// ────────────────────────────────────────────────────────────────────
function ReviewLeaveDialog({ reqId, currentUserId, isMaster, onClose, onDecided }) {
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/leave/requests/${reqId}/context`);
      setCtx(data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not load request");
      onClose();
    } finally {
      setLoading(false);
    }
  }, [reqId, onClose]);
  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-3xl">
          <div className="py-12 text-center text-slate-500 text-sm">Loading…</div>
        </DialogContent>
      </Dialog>
    );
  }
  if (!ctx) return null;

  const r = ctx.request;
  const isOwn = r.user_id === currentUserId;
  const canDecide = r.status === "pending" && (isMaster || (r.approver_id === currentUserId && !isOwn));

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/leave/requests/${r.id}/approve`, { reason: "" });
      toast.success("Leave approved");
      onDecided();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const reject = async () => {
    if (rejectReason.trim().length < 3) { toast.error("Add a rejection reason (min 3 chars)."); return; }
    setBusy(true);
    try {
      await api.post(`/leave/requests/${r.id}/reject`, { reason: rejectReason.trim() });
      toast.success("Leave rejected");
      onDecided();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const reassignMaster = async () => {
    setBusy(true);
    try {
      await api.post(`/leave/requests/${r.id}/reassign`, { assignee_id: "master" });
      toast.success("Reassigned to Master Admin");
      onDecided();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="leave-review-dialog">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2">
            <ShieldCheck size={18} /> Leave Request — {r.user_name}
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2 flex-wrap">
            <Badge className={STATUS_TONES[r.status] || ""}>{r.status}</Badge>
            <span className="text-[12px] text-slate-500">Submitted {fmtDateTime(r.created_at)}</span>
            {r.is_emergency && <Badge className="bg-rose-100 text-rose-800 dark:bg-rose-500/20 dark:text-rose-200">EMERGENCY</Badge>}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-2">
          {/* Left: main request info */}
          <div className="md:col-span-2 space-y-3">
            <div className="pk-card p-4 space-y-2">
              <div className="grid grid-cols-2 gap-3 text-[12.5px]">
                <div><div className="pk-table-header">Type</div><div className="font-semibold text-slate-900 dark:text-white">{r.kind_label || r.kind}</div></div>
                <div><div className="pk-table-header">Days</div><div className="mono font-semibold text-slate-900 dark:text-white">{r.leave_days}</div></div>
                <div><div className="pk-table-header">From</div><div className="text-slate-800 dark:text-slate-200">{fmt(r.from_date)}</div></div>
                <div><div className="pk-table-header">To</div><div className="text-slate-800 dark:text-slate-200">{fmt(r.to_date)}</div></div>
                <div><div className="pk-table-header">Employee</div><div className="text-slate-800 dark:text-slate-200">{r.user_name}<div className="text-[11px] mono text-slate-500">{r.employee_code || r.user_email}</div></div></div>
                <div><div className="pk-table-header">Approver</div><div className="text-slate-800 dark:text-slate-200">{r.approver_name || "—"}</div></div>
              </div>
              <div>
                <div className="pk-table-header">Reason</div>
                <div className="text-[13px] text-slate-800 dark:text-slate-200 whitespace-pre-wrap">{r.reason}</div>
              </div>
              {r.is_emergency && (
                <div className="pk-card p-3 border-rose-200 dark:border-rose-800 bg-rose-50/60 dark:bg-rose-900/20">
                  <div className="pk-table-header text-rose-800 dark:text-rose-200 flex items-center gap-1"><AlertTriangle size={12} /> Emergency reason</div>
                  <div className="text-[12.5px] text-rose-900 dark:text-rose-100 mt-0.5 whitespace-pre-wrap">{r.emergency_reason}</div>
                </div>
              )}
              {r.medical_cert_status !== "not_required" && (
                <div className="pk-card p-3 border-sky-200 dark:border-sky-800 bg-sky-50/60 dark:bg-sky-900/20">
                  <div className="pk-table-header text-sky-800 dark:text-sky-200 flex items-center gap-1"><FileText size={12} /> Medical certificate</div>
                  {r.medical_cert_link ? (
                    <a href={r.medical_cert_link} target="_blank" rel="noreferrer" className="text-[12.5px] text-sky-700 dark:text-sky-300 underline break-all">{r.medical_cert_link}</a>
                  ) : (
                    <div className="text-[12.5px] text-sky-900 dark:text-sky-100">Missing — extra days may be converted to LWP.</div>
                  )}
                </div>
              )}
              {r.status !== "pending" && r.decided_by_name && (
                <div className="text-[12px] text-slate-600 dark:text-slate-300 border-t border-slate-200 dark:border-slate-800 pt-2">
                  <b className="text-slate-900 dark:text-white capitalize">{r.status}</b> by {r.decided_by_name} on {fmtDateTime(r.decided_at)}
                  {r.decision_reason && <div className="text-[11.5px] italic mt-0.5">&ldquo;{r.decision_reason}&rdquo;</div>}
                </div>
              )}
            </div>
          </div>

          {/* Right: manager context */}
          <div className="space-y-3">
            <div className="pk-card p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users size={14} className="text-indigo-600" />
                <div className="text-[12.5px] font-bold text-slate-900 dark:text-white">YTD Approved Leaves</div>
              </div>
              <div className="text-3xl font-extrabold heading text-slate-900 dark:text-white mono">{ctx.ytd_total_days}</div>
              <div className="text-[11px] text-slate-500 mt-1">days approved this year</div>
              {Object.keys(ctx.ytd_by_kind || {}).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {Object.entries(ctx.ytd_by_kind).map(([k, v]) => (
                    <Badge key={k} className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-[10.5px]">{k}: {v}</Badge>
                  ))}
                </div>
              )}
            </div>

            {ctx.recent_leaves && ctx.recent_leaves.length > 0 && (
              <div className="pk-card p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock size={14} className="text-slate-600" />
                  <div className="text-[12.5px] font-bold text-slate-900 dark:text-white">Recent Leaves</div>
                </div>
                <ul className="space-y-1.5">
                  {ctx.recent_leaves.slice(0, 6).map((rc) => (
                    <li key={rc.id} className="flex items-center justify-between text-[11.5px] gap-2">
                      <div className="flex-1 truncate">
                        <span className="font-medium text-slate-800 dark:text-slate-200">{rc.kind_label || rc.kind}</span>
                        <span className="text-slate-500 dark:text-slate-400 ml-1">· {fmt(rc.from_date)} → {fmt(rc.to_date)}</span>
                      </div>
                      <Badge className={`${STATUS_TONES[rc.status] || ""} text-[9.5px] py-0 px-1.5`}>{rc.status}</Badge>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <ContextConflictList
              icon={ClipboardList}
              title="Task Conflicts"
              tone="amber"
              items={ctx.conflicts?.tasks || []}
              renderItem={(t, i) => (
                <li key={i} className="text-[11.5px]">
                  <span className="font-medium text-slate-800 dark:text-slate-200">{t.title || "Task"}</span>
                  <span className="text-slate-500 dark:text-slate-400"> · due {fmt(t.due_date)}</span>
                </li>
              )}
              empty="No overlapping tasks."
            />

            <ContextConflictList
              icon={Briefcase}
              title="CRM Follow-ups"
              tone="sky"
              items={ctx.conflicts?.crm || []}
              renderItem={(c, i) => (
                <li key={i} className="text-[11.5px]">
                  <span className="font-medium text-slate-800 dark:text-slate-200">{c.client_name || "Client"}</span>
                  <span className="text-slate-500 dark:text-slate-400"> · {fmt(c.next_followup_date)}</span>
                </li>
              )}
              empty="No pending follow-ups on these dates."
            />

            <ContextConflictList
              icon={BookOpen}
              title="Training / Sessions"
              tone="violet"
              items={ctx.conflicts?.training || []}
              renderItem={(tr, i) => (
                <li key={i} className="text-[11.5px]">
                  <span className="font-medium text-slate-800 dark:text-slate-200">{tr.title || "Training"}</span>
                  <span className="text-slate-500 dark:text-slate-400"> · {fmt(tr.session_date || tr.start_date || tr.scheduled_at)}</span>
                </li>
              )}
              empty="No training conflicts."
            />
          </div>
        </div>

        {canDecide && (
          <div className="border-t border-slate-200 dark:border-slate-800 pt-4 mt-2">
            {!showReject ? (
              <div className="flex flex-wrap items-center justify-end gap-2">
                <Button variant="outline" onClick={reassignMaster} disabled={busy} data-testid="leave-review-reassign">
                  Reassign to Master Admin
                </Button>
                <Button variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => setShowReject(true)} disabled={busy} data-testid="leave-review-reject-open">
                  <XCircle size={14} className="mr-1.5" /> Reject
                </Button>
                <Button onClick={approve} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="leave-review-approve">
                  <CheckCircle2 size={14} className="mr-1.5" /> {busy ? "Approving…" : "Approve"}
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <Label className="text-[11px] uppercase tracking-wider font-semibold">Rejection reason</Label>
                <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={2} placeholder="Reason to reject (min 3 chars)…" data-testid="leave-review-reject-reason" />
                <div className="flex items-center justify-end gap-2">
                  <Button variant="outline" onClick={() => { setShowReject(false); setRejectReason(""); }} disabled={busy}>Cancel</Button>
                  <Button onClick={reject} disabled={busy} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="leave-review-reject-confirm">
                    {busy ? "Rejecting…" : "Confirm Reject"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {!canDecide && r.status === "pending" && isOwn && (
          <div className="text-[12px] text-slate-500 italic border-t border-slate-200 dark:border-slate-800 pt-3 mt-2">
            You submitted this request — someone else must review it.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ContextConflictList({ icon: Icon, title, tone, items, renderItem, empty }) {
  const toneMap = {
    amber:  { icon: "text-amber-600",  badge: "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200" },
    sky:    { icon: "text-sky-600",    badge: "bg-sky-100 text-sky-800 dark:bg-sky-500/20 dark:text-sky-200" },
    violet: { icon: "text-violet-600", badge: "bg-violet-100 text-violet-800 dark:bg-violet-500/20 dark:text-violet-200" },
  };
  const t = toneMap[tone] || toneMap.amber;
  return (
    <div className="pk-card p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className={t.icon} />
        <div className="text-[12.5px] font-bold text-slate-900 dark:text-white">{title}</div>
        {items.length > 0 && <Badge className={`${t.badge} text-[10px]`}>{items.length}</Badge>}
      </div>
      {items.length === 0 ? (
        <div className="text-[11.5px] text-slate-400 italic">{empty}</div>
      ) : (
        <ul className="space-y-1">{items.map(renderItem)}</ul>
      )}
    </div>
  );
}
